import {
  thumbHashToApproximateAspectRatio,
  thumbHashToDataURL,
} from "thumbhash";
import { base64ToUint8Array } from "@/lib/dataConversions";

type PlaceholderMeta = {
  width?: number;
  height?: number;
  placeholder?: string;
};

/**
 * Media aspect ratio (width ÷ height) for sizing a message bubble, via a
 * backward-compatible ladder:
 *   ① exact width/height in the meta → precise ratio
 *   ② ThumbHash placeholder only → the hash's approximate ratio
 *   ③ neither (legacy messages / decode failure) → null
 *
 * On null the caller falls back to the thumbnail's own natural ratio once it
 * loads (a neutral square until then), so historical messages with no embedded
 * dimensions still render at the right shape. The bubble fills its column up to
 * a max width and caps its height in CSS — only the ratio is needed here, not a
 * pixel box.
 */
export function getMediaAspect(meta: PlaceholderMeta): number | null {
  // ① exact dimensions
  if (meta.width && meta.height && meta.width > 0 && meta.height > 0) {
    return meta.width / meta.height;
  }
  // ② ThumbHash approximate aspect
  if (meta.placeholder) {
    try {
      const ratio = thumbHashToApproximateAspectRatio(
        base64ToUint8Array(meta.placeholder),
      );
      if (Number.isFinite(ratio) && ratio > 0) {
        return ratio;
      }
    } catch {
      /* fall through to null */
    }
  }
  // ③ legacy / decode failure — caller uses the thumbnail's natural ratio
  return null;
}

/**
 * Decode a base64 ThumbHash into a PNG data URL for an instant blurred
 * placeholder. Pure (no canvas) — thumbHashToDataURL builds the PNG in JS, so
 * this works before any attachment fetch. Returns null on a legacy/absent or
 * malformed placeholder.
 */
export function placeholderToDataURL(placeholder?: string): string | null {
  if (!placeholder) {
    return null;
  }
  try {
    return thumbHashToDataURL(base64ToUint8Array(placeholder));
  } catch {
    return null;
  }
}
